import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CodeEditor from './CodeEditor';
import ReactMarkdown from 'react-markdown';
import { judgeService } from '../services/JudgeService';

interface ProblemLayoutProps {
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  tags: string[];
  description: string;
  hints: string[];
  code: string;
  onCodeChange: (code: string) => void;
  onResetCode: () => void;
  backUrl: string;
  problemId: string;
}

const ProblemLayout: React.FC<ProblemLayoutProps> = ({
  title,
  difficulty,
  tags,
  description,
  hints,
  code,
  onCodeChange,
  onResetCode,
  backUrl,
  problemId
}) => {
  const navigate = useNavigate();
  const [showHints, setShowHints] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState<{
    stdout?: string;
    stderr?: string;
    error?: string;
  }>({});

  const handleRun = async () => {
    setIsRunning(true);
    try {
      const result = await judgeService.runCode(code);
      setOutput({
        stdout: result.stdout,
        stderr: result.stderr,
        error: result.code !== 0 ? result.stderr || result.output : undefined
      });
    } catch (error) {
      setOutput({ error: 'Failed to run code. Please try again.' });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="h-screen bg-[#1a1a1a] text-white flex flex-col">
      {/* Header */}
      <div className="h-14 bg-[#2d2d2d] border-b border-white/10 flex items-center justify-between px-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(backUrl)}
            className="text-white/70 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-lg font-medium">{title}</h1>
          <span className={`px-2 py-0.5 rounded text-xs ${
            difficulty === 'Easy' ? 'bg-[#2cbb5d]/20 text-[#2cbb5d]' :
            difficulty === 'Medium' ? 'bg-[#ffa116]/20 text-[#ffa116]' :
            'bg-[#ef4743]/20 text-[#ef4743]'
          }`}>
            {difficulty}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel */}
        <div className="w-[45%] flex flex-col border-r border-white/10">
          {/* Tabs */}
          <div className="h-10 flex border-b border-white/10">
            <button
              className={`px-4 h-full text-sm font-medium text-white border-b-2 border-[#2cbb5d]`}
            >
              Description
            </button>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-auto">
            <div className="p-4 space-y-4">
              <div className="prose prose-invert max-w-none">
                <ReactMarkdown>{description}</ReactMarkdown>
              </div>

              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-[#2d2d2d] rounded text-xs text-white/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="border border-white/10 rounded-md overflow-hidden">
                <button
                  onClick={() => setShowHints(!showHints)}
                  className="w-full flex items-center justify-between p-3 bg-[#2d2d2d] hover:bg-[#3d3d3d]"
                >
                  <span className="font-medium">Hints</span>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${showHints ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showHints && (
                  <div className="p-3 space-y-2">
                    {hints.map((hint, index) => (
                      <p key={index} className="text-white/70">
                        {index + 1}. {hint}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right panel - Code editor */}
        <div className="flex-1">
          <CodeEditor
            code={code}
            language="javascript"
            onChange={onCodeChange}
            problemId={problemId}
          />
          {output.stdout && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-white/90 mb-2">Output:</h3>
              <pre className="bg-[#2d2d2d] p-3 rounded font-mono text-sm whitespace-pre-wrap text-white/80">
                {output.stdout}
              </pre>
            </div>
          )}
          {output.stderr && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-[#ef4743] mb-2">Errors:</h3>
              <pre className="bg-[#2d2d2d] p-3 rounded font-mono text-sm whitespace-pre-wrap text-[#ef4743]/80">
                {output.stderr}
              </pre>
            </div>
          )}
          {output.error && (
            <div className="p-4 bg-[#ef4743]/10 border border-[#ef4743]/20 rounded">
              <p className="text-[#ef4743]">{output.error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemLayout;
